import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { AuthService, UserProfile } from '../services/AuthService';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  isAdmin: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, profile: Omit<UserProfile, 'uid' | 'email' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  registerAdmin: (email: string, password: string, profile: any) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (uid: string, updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Firestore / Auth permission failures after password change invalidate the session. */
const isPermissionDeniedError = (error: unknown): boolean => {
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code?: string }).code ?? '')
      : '';
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    code === 'permission-denied' ||
    code === 'firestore/permission-denied' ||
    /permission-denied|insufficient permissions/i.test(code) ||
    /permission-denied|insufficient permissions/i.test(message)
  );
};

const isProfileNotFoundError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /profile not found/i.test(message);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.error('useAuth called outside of AuthProvider');
    // Return a default context instead of throwing
    return {
      currentUser: null,
      userProfile: null,
      isAdmin: false,
      loading: true,
      login: async () => {},
      register: async () => {},
      registerAdmin: async () => {},
      logout: async () => {},
      updateProfile: async () => {},
      refreshProfile: async () => {},
    };
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initialAuthCheckComplete, setInitialAuthCheckComplete] = useState(false);

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setInitialAuthCheckComplete(true);
      // Don't set loading to false here, wait for profile loading
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (currentUser) {
        // Retry only for new-registration race (profile doc not written yet).
        // Do not retry permission-denied — that means the session is dead.
        const maxRetries = 5;
        const retryDelay = 500; // ms
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const profile = await AuthService.getCurrentUserProfile();
            setUserProfile(profile);
            setIsAdmin(profile?.isAdmin === true);
            
            // Success - exit retry loop
            setLoading(false);
            return;
          } catch (error) {
            if (isPermissionDeniedError(error)) {
              console.error('Permission denied loading profile; signing out dead session:', error);
              try {
                // Auth-only sign-out; no Firestore write
                await AuthService.signOutUser();
              } catch (signOutError) {
                console.error('Sign-out after permission denial failed:', signOutError);
              }
              setCurrentUser(null);
              setUserProfile(null);
              setIsAdmin(false);
              setLoading(false);
              return;
            }

            // Retry only when the profile doc is missing (registration race)
            if (isProfileNotFoundError(error) && attempt < maxRetries) {
              console.log(`Profile not found, retrying in ${retryDelay}ms (attempt ${attempt}/${maxRetries})`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              continue;
            }

            // Non-retryable error, or final profile-not-found attempt
            console.error('Failed to load user profile:', error);
            setUserProfile(null);
            setIsAdmin(false);
            break;
          }
        }
      } else {
        setUserProfile(null);
        setIsAdmin(false);
      }
      // Set loading to false after profile loading is complete
      setLoading(false);
    };

    // Only start loading profile after initial auth check is complete
    if (initialAuthCheckComplete) {
      loadUserProfile();
    }
  }, [currentUser, initialAuthCheckComplete]);

  const login = async (email: string, password: string) => {
    await AuthService.loginUser(email, password);
  };

  const register = async (email: string, password: string, profile: Omit<UserProfile, 'uid' | 'email' | 'createdAt' | 'updatedAt'>) => {
    await AuthService.registerUser(email, password, profile);
  };

  const registerAdmin = async (email: string, password: string, profile: any) => {
    await AuthService.registerAdminUser(email, password, profile);
  };

  const logout = async () => {
    await AuthService.logoutUser();
  };

  const updateProfile = async (uid: string, updates: Partial<UserProfile>) => {
    await AuthService.updateUserProfile(uid, updates);
    // Reload the profile after update
    if (currentUser) {
      const updatedProfile = await AuthService.getCurrentUserProfile();
      setUserProfile(updatedProfile);
    }
  };

  const refreshProfile = async () => {
    if (!currentUser) {
      return;
    }
    const updatedProfile = await AuthService.getCurrentUserProfile();
    setUserProfile(updatedProfile);
    setIsAdmin(updatedProfile?.isAdmin === true);
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    isAdmin,
    loading,
    login,
    register,
    registerAdmin,
    logout,
    updateProfile,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 